/* HOVE unit: procedural three.js model of the neck massager, shared by the page-wide 3D stage
   and the inline viewers. Fine detail (seams, grooves, inset panels, debossed logo, soft-touch
   cushions) is baked into textures in the band's UV space so it stays crisp at any size. */
(function () {
  if (window.HoveUnit) return;

  const sm = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
  const lerp = (a, b, t) => a + (b - a) * t;
  const POD0 = .5, POD1 = .66, TIP = .105;
  // centerline seen from above: wide arc behind the neck, pods curl in toward each other
  const RP = [[0, -1.0], [.52, -.86], [.87, -.5], [1.0, -.02], [.97, .38], [.86, .68], [.71, .9], [.56, 1.02]];
  const PAD_U = .165; // where the pad necks leave the band
  const CUSH_A = .6, CUSH_B = .985;
  const PANEL_C = .735, PANEL_HALF = .145, PANEL_R = .075; // starts at the pod flare, ends above the button // dished panel on the outer face of each pod // cushion runs from just below the seam to the tip

  // cross-section at arc position s (0 = back center, 1 = tip)
  function section(s) {
    const g = sm(POD0, POD1, s);
    let w = lerp(.12, .41, g), h = lerp(.46, .48, g);
    const e = Math.max(0, (s - (1 - TIP)) / TIP), c = Math.sqrt(Math.max(0, 1 - e * e));
    h *= c; w *= Math.pow(c, .85);
    // cross-section relaxes to a plain ellipse toward the end so the cap is a smooth dome
    return { g, w, h, off: (lerp(.12, .41, g) - .12) * .42, nx: lerp(lerp(3.4, 2.02, Math.sqrt(g)), 1.95, sm(0, 1, e)) };
  }
  const spow = (v, p) => Math.sign(v) * Math.pow(Math.abs(v), p);
  // signed distance to a pill laid along the pod (real units)
  const stadium = (ds, dy, half, r) => { const q = Math.max(Math.abs(ds) - (half - r), 0); return Math.hypot(q, dy) - r; };
  const edgeAt = (g) => lerp(.5, .12, g); // where the finish color starts (cos of section angle)

  /* ---------- baked detail maps (shared canvases, cached) ---------- */
  let bakeCache = null;
  function logoMask() {
    const c = document.createElement('canvas'); c.width = 560; c.height = 132;
    const g = c.getContext('2d'); g.scale(.5, .5); g.translate(0, 2);
    g.strokeStyle = '#fff'; g.lineWidth = 30; g.lineCap = 'round'; g.lineJoin = 'round';
    ['M16 16V244M226 16V244M16 116H226', 'M580 18L705 240L830 18', 'M1090 18H902V242H1100M902 130H1075'].forEach((d) => g.stroke(new Path2D(d)));
    g.lineCap = 'butt'; g.stroke(new Path2D('M300 104A136 136 0 0 1 554 104'));
    g.lineCap = 'round'; g.lineWidth = 26; g.stroke(new Path2D('M296 158 Q304 222 370 242 M558 158 Q550 222 484 242'));
    const d = g.getImageData(0, 0, c.width, c.height).data, a = new Float32Array(c.width * c.height);
    for (let i = 0; i < a.length; i++) a[i] = d[i * 4 + 3] / 255;
    return { a, w: c.width, h: c.height };
  }
  function bake(HL, W, H) {
    if (bakeCache && bakeCache.W === W) return bakeCache;
    const logo = logoMask();
    const LOGO_W = .5, LOGO_H = LOGO_W * logo.h / logo.w;
    const hgt = new Float32Array(W * H), ao = new Float32Array(W * H), rough = new Float32Array(W * H), coat = new Float32Array(W * H);
    const du = 2 * HL / W, dvs = new Float32Array(W);
    for (let x = 0; x < W; x++) {
      const t = (x + .5) / W, u = 2 * t - 1, s = Math.abs(u);
      const S = section(s), hh = S.h / 2, ww = S.w / 2, edge = edgeAt(S.g);
      dvs[x] = Math.max(.02, 2 * (S.w + S.h) * .93) / H;
      for (let y = 0; y < H; y++) {
        const f = (y + .5) / H * Math.PI * 2 + Math.PI, cs = Math.cos(f), sn = Math.sin(f);
        const yp = spow(sn, 2 / S.nx) * hh;
        let hv = 0, a = 1, r = .3, cc = .75;
        const inner = sm(.25, .5, -cs);
        r = lerp(r, .6, inner); cc = lerp(cc, .12, inner);
        a *= 1 - .07 * inner;
        const angPhys = (target) => Math.abs(cs - target) / Math.max(.25, Math.abs(sn)) * Math.min(ww, hh + .02);
        // parting line where the finish color meets the white face
        { const d = angPhys(edge), k = Math.exp(-Math.pow(d / .0032, 2)); hv -= .0012 * k; a *= 1 - .32 * k; }
        // parting line between the white face and the grey inner lining
        { const d = angPhys(-.3), k = Math.exp(-Math.pow(d / .003, 2)); hv -= .0009 * k; a *= 1 - .22 * k; }
        // seam where band meets pod
        { const ds = (s - POD0) * HL, k = Math.exp(-Math.pow(ds / .0035, 2)); hv -= .003 * k; a *= 1 - .6 * k; }
        if (S.g > .5 && hh > 0) {
          if (cs < -.1) {
            // contact shadow where the separate cushion meets the shell
            const half = (CUSH_B - CUSH_A) / 2 * HL, d = stadium((s - (CUSH_A + CUSH_B) / 2) * HL, yp, half, .74 * hh), k = sm(-.1, -.5, cs);
            a *= 1 - .42 * Math.exp(-Math.pow(d / .02, 2)) * k;
          }
          if (cs > .3) {
            // pill-shaped inset panel on the outer face
            const d = stadium((s - PANEL_C) * HL, yp, PANEL_HALF * HL, PANEL_R), k = sm(.3, .65, cs);
            if (d < .01) { a *= 1 - .11 * k * Math.exp(-Math.pow((d + .01) / .014, 2)); r = lerp(r, .36, k * sm(.01, -.03, d)); } // soft shade where the recess slopes in
          }
        }
        // debossed wordmark on the back of the band
        if (s < .2 && cs > .55) {
          const lx = (-u * HL) / LOGO_W + .5, ly = .5 - yp / LOGO_H;
          if (lx > 0 && lx < 1 && ly > 0 && ly < 1) {
            const m = logo.a[Math.floor(ly * logo.h) * logo.w + Math.floor(lx * logo.w)] * sm(.55, .8, cs);
            hv -= .0016 * m; a *= 1 - .16 * m; r = lerp(r, .55, m); cc = lerp(cc, .2, m);
          }
        }
        // occlusion where the pad necks meet the band
        if (s < .35 && cs < -.2) {
          const dx = (Math.abs(u) - PAD_U) * HL, rr = Math.hypot(dx, yp);
          a *= 1 - .38 * Math.exp(-Math.pow(rr / .1, 2)) * sm(.2, .8, -cs);
        }
        // gentle darkening toward the top/bottom edges of the pods
        a *= 1 - .05 * S.g * Math.pow(Math.abs(sn), 6);
        const i = y * W + x; hgt[i] = hv; ao[i] = a; rough[i] = r; coat[i] = cc;
      }
    }
    const mk = () => { const c = document.createElement('canvas'); c.width = W; c.height = H; return c; };
    const nC = mk(), aC = mk(), rC = mk();
    const nI = nC.getContext('2d').createImageData(W, H), aI = aC.getContext('2d').createImageData(W, H), rI = rC.getContext('2d').createImageData(W, H);
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      const i = y * W + x, xl = y * W + Math.max(0, x - 1), xr = y * W + Math.min(W - 1, x + 1);
      const yu = ((y + H - 1) % H) * W + x, yd = ((y + 1) % H) * W + x;
      const dx = (hgt[xr] - hgt[xl]) / (2 * du), dy = (hgt[yd] - hgt[yu]) / (2 * dvs[x]);
      let nx = -dx, ny = -dy, nz = 1; const l = Math.hypot(nx, ny, nz); nx /= l; ny /= l; nz /= l;
      const o = i * 4;
      nI.data[o] = (nx * .5 + .5) * 255; nI.data[o + 1] = (ny * .5 + .5) * 255; nI.data[o + 2] = (nz * .5 + .5) * 255; nI.data[o + 3] = 255;
      const av = ao[i] * 255; aI.data[o] = aI.data[o + 1] = aI.data[o + 2] = av; aI.data[o + 3] = 255;
      rI.data[o] = coat[i] * 255; rI.data[o + 1] = rough[i] * 255; rI.data[o + 2] = 0; rI.data[o + 3] = 255;
    }
    nC.getContext('2d').putImageData(nI, 0, 0); aC.getContext('2d').putImageData(aI, 0, 0); rC.getContext('2d').putImageData(rI, 0, 0);
    bakeCache = { W, normal: nC, ao: aC, rc: rC };
    return bakeCache;
  }

  function roundedShape(THREE, w, h, r) { const s = new THREE.Shape(), x = -w / 2, y = -h / 2; s.moveTo(x + r, y); s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r); s.lineTo(x + w, y + h - r); s.quadraticCurveTo(x + w, y + h, x + w - r, y + h); s.lineTo(x + r, y + h); s.quadraticCurveTo(x, y + h, x, y + h - r); s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y); return s; }

  /* ---------- studio lighting environment ---------- */
  function softTex(THREE) {
    const c = document.createElement('canvas'); c.width = c.height = 128; const g = c.getContext('2d');
    const gr = g.createRadialGradient(64, 64, 10, 64, 64, 64); gr.addColorStop(0, '#fff'); gr.addColorStop(.6, '#fff'); gr.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = gr; g.fillRect(0, 0, 128, 128); return new THREE.CanvasTexture(c);
  }
  function studioEnv(THREE, renderer) {
    const es = new THREE.Scene();
    const c = document.createElement('canvas'); c.width = 4; c.height = 256; const g = c.getContext('2d');
    const gr = g.createLinearGradient(0, 0, 0, 256); gr.addColorStop(0, '#d9dce0'); gr.addColorStop(.45, '#9ea3a9'); gr.addColorStop(.55, '#6f747a'); gr.addColorStop(1, '#2b2e32');
    g.fillStyle = gr; g.fillRect(0, 0, 4, 256);
    const dome = new THREE.CanvasTexture(c); dome.encoding = THREE.sRGBEncoding;
    es.add(new THREE.Mesh(new THREE.SphereGeometry(20, 32, 16), new THREE.MeshBasicMaterial({ map: dome, color: new THREE.Color(.16, .16, .16), side: THREE.BackSide })));
    const st = softTex(THREE);
    const box = (w, h, x, y, z, i) => { const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color(i, i, i), side: THREE.DoubleSide })); m.position.set(x, y, z); m.lookAt(0, 0, 0); es.add(m); };
    box(7, 5, -6, 7, 5, 5);      // key softbox, upper left front
    box(13, 2.4, 0, 9.5, 0, 3.2); // overhead strip
    box(1.6, 10, -9.5, 1, 1, 4);  // tall strip left: long highlights on the glossy shell
    box(1.6, 10, 9.5, 1, -1, 2.6);// tall strip right
    box(10, 5, 0, 1, 10, 1.1);    // front fill
    box(12, 12, 0, -8, 0, .35);   // floor bounce
    const pm = new THREE.PMREMGenerator(renderer);
    const tex = pm.fromScene(es, .02).texture; pm.dispose();
    return tex;
  }

  /* ---------- the unit ---------- */
  function build(THREE, opts = {}) {
    const lin = (h) => new THREE.Color(h).convertSRGBToLinear();
    const toLin = (h) => { try { return lin(h || '#F2F3F4'); } catch (e) { return lin('#F2F3F4'); } };
    const up = new THREE.Vector3(0, 1, 0);
    const cpts = [];
    for (let i = RP.length - 1; i > 0; i--) cpts.push(new THREE.Vector3(-RP[i][0], 0, RP[i][1]));
    RP.forEach((r) => cpts.push(new THREE.Vector3(r[0], 0, r[1])));
    const curve = new THREE.CatmullRomCurve3(cpts, false, 'centripetal');
    const HL = curve.getLength() / 2;
    const lite = !!opts.lite;
    const NS = lite ? 420 : 640, MS = lite ? 96 : 128;

    const frames = [], pos = [], uvs = [], meta = [], idx = [];
    for (let i = 0; i <= NS; i++) {
      const t = i / NS, u = 2 * t - 1, s = Math.abs(u);
      const p = curve.getPointAt(t), T = curve.getTangentAt(t); T.y = 0; T.normalize();
      const N = new THREE.Vector3().crossVectors(up, T).normalize();
      const S = section(s);
      frames.push({ t, u, p, N, T, w: S.w, h: S.h, off: S.off });
      for (let j = 0; j <= MS; j++) {
        const f = j / MS * Math.PI * 2 + Math.PI, cs = Math.cos(f), sn = Math.sin(f);
        let x = spow(cs, 2 / S.nx) * S.w / 2;
        const y = spow(sn, 2 / S.nx) * S.h / 2;
        const cush = 0;
        if (S.g > .5 && S.h > 0 && cs > .25) {
          const d = stadium((s - PANEL_C) * HL, y, PANEL_HALF * HL, PANEL_R), k = sm(.25, .6, cs);
          // long, shallow pill-shaped recess with soft edges (no lip)
          x -= .0095 * k * sm(.008, -.025, d);
        }
        pos.push(p.x + N.x * (S.off + x), y, p.z + N.z * (S.off + x));
        uvs.push(t, j / MS);
        meta.push(cs, s, cush);
      }
    }
    const R = MS + 1;
    for (let i = 0; i < NS; i++) for (let j = 0; j < MS; j++) { const a = i * R + j, b = a + 1, c = a + R, d = c + 1; idx.push(a, b, c, b, d, c); }
    const bandGeo = new THREE.BufferGeometry();
    bandGeo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    bandGeo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    const colArr = new Float32Array((pos.length / 3) * 3); bandGeo.setAttribute('color', new THREE.BufferAttribute(colArr, 3));
    bandGeo.setIndex(idx); bandGeo.computeVertexNormals();
    // weld normals across the UV seam (inner face) so there is no shading line
    { const n = bandGeo.attributes.normal; for (let i = 0; i <= NS; i++) { const a = i * R, b = i * R + MS; const x = n.getX(a) + n.getX(b), y = n.getY(a) + n.getY(b), z = n.getZ(a) + n.getZ(b), l = Math.hypot(x, y, z) || 1; n.setXYZ(a, x / l, y / l, z / l); n.setXYZ(b, x / l, y / l, z / l); } }

    const BW = lite ? 1024 : 2048, BH = lite ? 256 : 512;
    const bk = bake(HL, BW, BH);
    const tex = (c, srgb) => { const t = new THREE.CanvasTexture(c); t.flipY = false; t.wrapT = THREE.RepeatWrapping; t.anisotropy = 8; if (srgb) t.encoding = THREE.sRGBEncoding; t.generateMipmaps = true; return t; };
    const aoTex = tex(bk.ao), nTex = tex(bk.normal), rcTex = tex(bk.rc);
    const shellMat = new THREE.MeshPhysicalMaterial({
      vertexColors: true, map: aoTex, normalMap: nTex, normalScale: new THREE.Vector2(1, 1),
      roughness: 1, roughnessMap: rcTex, metalness: 0, clearcoat: 1, clearcoatMap: rcTex, clearcoatRoughness: .2, envMapIntensity: .95
    });

    const WHITE = lin('#EEF0F2'), LINING = lin('#C6CACF'), CUSHION = lin('#D2D5DA');
    function paint(ac) {
      const tmp = new THREE.Color();
      for (let v = 0; v < meta.length / 3; v++) {
        const cs = meta[v * 3], s = meta[v * 3 + 1], cush = meta[v * 3 + 2];
        const g = sm(POD0, POD1, s), edge = edgeAt(g);
        const ko = sm(edge - .025, edge + .025, cs), ki = sm(.2, .4, -cs);
        tmp.copy(WHITE).lerp(ac, ko).lerp(LINING, ki * (1 - cush)).lerp(CUSHION, cush);
        colArr[v * 3] = tmp.r; colArr[v * 3 + 1] = tmp.g; colArr[v * 3 + 2] = tmp.b;
      }
      bandGeo.attributes.color.needsUpdate = true;
    }
    const acCur = toLin(opts.accent), acFrom = acCur.clone(), acTo = acCur.clone();
    let acT = 1;
    paint(acCur);

    const body = new THREE.Group(); body.position.z = .08;
    const band = new THREE.Mesh(bandGeo, shellMat); body.add(band);
    const frameAt = (u) => frames[Math.round((u + 1) / 2 * NS)];

    const chromeMat = new THREE.MeshStandardMaterial({ color: 0xffffff, metalness: 1, roughness: .06, envMapIntensity: 1.6 });
    const padMat = new THREE.MeshStandardMaterial({ color: lin('#E4E6E9'), metalness: 1, roughness: .1, envMapIntensity: 1.5, emissive: new THREE.Color(0xff5a1f), emissiveIntensity: 0 });
    const housingMat = new THREE.MeshPhysicalMaterial({ color: lin('#DADDE1'), roughness: .5, clearcoat: .25, clearcoatRoughness: .4 });
    const stalkMat = new THREE.MeshPhysicalMaterial({ color: lin('#D3D6DA'), roughness: .55, clearcoat: .15 });
    const greyMat = new THREE.MeshPhysicalMaterial({ color: lin('#B7BBC1'), roughness: .45, clearcoat: .3 });
    const bend = (geo, k) => { const a = geo.attributes.position; for (let i = 0; i < a.count; i++) { const x = a.getX(i); a.setZ(i, a.getZ(i) + k * x * x); } geo.computeVertexNormals(); return geo; };
    function padPath(rx, ry, hole) {
      const s = hole ? new THREE.Path() : new THREE.Shape(), n = 96;
      for (let i = 0; i <= n; i++) {
        const a = (hole ? -1 : 1) * i / n * Math.PI * 2, c = Math.cos(a), si = Math.sin(a);
        const x = spow(c, 2 / 2.35) * rx, y = spow(si, 2 / 2.35) * ry * (1 - .12 * Math.max(0, -si));
        i ? s.lineTo(x, y) : s.moveTo(x, y);
      }
      return s;
    }
    function glowTex(inner, outer) { const c = document.createElement('canvas'); c.width = c.height = 128; const g = c.getContext('2d'); const gr = g.createRadialGradient(64, 64, 0, 64, 64, 64); gr.addColorStop(0, inner); gr.addColorStop(.45, outer); gr.addColorStop(1, 'rgba(0,0,0,0)'); g.fillStyle = gr; g.fillRect(0, 0, 128, 128); const t = new THREE.CanvasTexture(c); t.encoding = THREE.sRGBEncoding; return t; }
    const heatTex = glowTex('rgba(255,190,120,1)', 'rgba(255,90,30,.45)');
    const glows = [], rings = [];

    [-1, 1].forEach((side) => {
      const f = frameAt(side * PAD_U);
      const inward = f.N.clone().negate();
      const base = f.p.clone().addScaledVector(f.N, f.off - f.w / 2);
      const center = base.clone().addScaledVector(inward, .3); // neck leaves the band square-on
      const dir = inward.clone().add(new THREE.Vector3(-side * .22, -.04, .1)).normalize();
      const look = center.clone().add(dir);
      // waisted, slightly flattened neck: flares into the band and into the pad
      const sv = center.clone().sub(base), len = sv.length() * .82;
      const prof = []; for (let k = 0; k <= 24; k++) { const tt = k / 24; prof.push(new THREE.Vector2(.058 + .075 * Math.pow(2 * tt - 1, 2) + .035 * tt, (tt - .5) * len)); }
      const st = new THREE.Mesh(new THREE.LatheGeometry(prof, 64), stalkMat);
      st.position.copy(base).addScaledVector(sv, .41); st.quaternion.setFromUnitVectors(up, sv.clone().normalize());
      st.scale.set(1.3, 1, .8); body.add(st);
      const pad = new THREE.Group(); pad.position.copy(center); pad.lookAt(look); body.add(pad);
      const back = new THREE.Mesh(bend(new THREE.ExtrudeGeometry(padPath(.25, .205), { depth: .018, bevelEnabled: true, bevelThickness: .022, bevelSize: .02, bevelSegments: 10, curveSegments: 96 }), .7), housingMat);
      back.position.z = -.05; pad.add(back);
      // thin dark gasket between housing and chrome rim
      const gs = padPath(.252, .207); gs.holes.push(padPath(.238, .193, true));
      const gasket = new THREE.Mesh(bend(new THREE.ExtrudeGeometry(gs, { depth: .01, bevelEnabled: false, curveSegments: 96 }), .7), new THREE.MeshStandardMaterial({ color: lin('#8E939A'), roughness: .6 }));
      gasket.position.z = -.02; pad.add(gasket);
      const rimShape = padPath(.245, .2); rimShape.holes.push(padPath(.21, .168, true));
      const rim = new THREE.Mesh(bend(new THREE.ExtrudeGeometry(rimShape, { depth: .008, bevelEnabled: true, bevelThickness: .013, bevelSize: .011, bevelSegments: 8, curveSegments: 96 }), .7), chromeMat);
      rim.position.z = -.014; pad.add(rim);
      const plate = new THREE.Mesh(bend(new THREE.ExtrudeGeometry(padPath(.214, .172), { depth: .003, bevelEnabled: true, bevelThickness: .006, bevelSize: .005, bevelSegments: 5, curveSegments: 96 }), .7), padMat);
      plate.position.z = -.013; pad.add(plate);
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: heatTex, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false, opacity: 0 }));
      sp.position.copy(center).addScaledVector(dir, .12); body.add(sp); glows.push(sp);
      const rg = new THREE.Group(); rg.position.copy(center); rg.lookAt(look); rg.translateZ(.03); body.add(rg);
      for (let k = 0; k < 3; k++) { const m = new THREE.Mesh(new THREE.RingGeometry(.94, 1, 72), new THREE.MeshBasicMaterial({ color: 0x8fd8ff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide })); rg.add(m); rings.push({ m, k }); }
    });

    // soft-touch cushions: thick rounded pillows along the inner face of each pod, wrapping to the tip
    const cushMat = new THREE.MeshPhysicalMaterial({ color: lin('#C9CDD2'), roughness: .76, clearcoat: .04, clearcoatRoughness: .6, envMapIntensity: .75 });
    [-1, 1].forEach((side) => {
      const NL = lite ? 110 : 170, NC = lite ? 56 : 72, cp = [], ci = [];
      for (let i = 0; i <= NL; i++) {
        const s = CUSH_A + (CUSH_B - CUSH_A) * i / NL, u = side * s, t = (u + 1) / 2;
        const p = curve.getPointAt(t), T = curve.getTangentAt(t); T.y = 0; T.normalize();
        const N = new THREE.Vector3().crossVectors(up, T).normalize();
        const S = section(s);
        const e = Math.max(Math.max(0, 1 - (s - CUSH_A) / .075), Math.max(0, 1 - (CUSH_B - s) / .08));
        const c = Math.sqrt(Math.max(0, 1 - e * e));
        const shellTaper = Math.sqrt(Math.min(1, S.h / .5));
        const a = .058 * Math.pow(c, .5) * shellTaper, b = Math.max(.001, .8 * (S.h / 2 + .02)) * c;
        const xc = S.off - S.w / 2 + .05 * a;
        for (let j = 0; j < NC; j++) {
          const f = j / NC * Math.PI * 2, cx = spow(Math.cos(f), 2 / 2.3) * a, cy = spow(Math.sin(f), 2 / 2.3) * b;
          cp.push(p.x + N.x * (xc + cx), cy, p.z + N.z * (xc + cx));
        }
      }
      for (let i = 0; i < NL; i++) for (let j = 0; j < NC; j++) { const a = i * NC + j, b = i * NC + (j + 1) % NC, c = a + NC, d = b + NC; ci.push(a, b, c, b, d, c); }
      const cg = new THREE.BufferGeometry(); cg.setAttribute('position', new THREE.Float32BufferAttribute(cp, 3)); cg.setIndex(ci); cg.computeVertexNormals();
      { // the two sides run the curve in opposite directions; flip winding if normals point inward
        const mid = Math.floor(NL / 2) * NC, pa = cg.attributes.position, na = cg.attributes.normal;
        let cx = 0, cy = 0, cz = 0; for (let j = 0; j < NC; j++) { cx += pa.getX(mid + j); cy += pa.getY(mid + j); cz += pa.getZ(mid + j); }
        cx /= NC; cy /= NC; cz /= NC;
        const v = mid + Math.floor(NC / 4);
        if ((pa.getX(v) - cx) * na.getX(v) + (pa.getY(v) - cy) * na.getY(v) + (pa.getZ(v) - cz) * na.getZ(v) < 0) {
          for (let q = 0; q < ci.length; q += 3) { const t = ci[q + 1]; ci[q + 1] = ci[q + 2]; ci[q + 2] = t; }
          cg.setIndex(ci); cg.computeVertexNormals();
        }
      }
      body.add(new THREE.Mesh(cg, cushMat));
    });

    // power button: long polished-chrome pill at the lower end of the right pod's recess, aligned with it
    (function () {
      const f = frameAt(.846); const pnt = f.p.clone().addScaledVector(f.N, f.off + f.w / 2 - .0085); pnt.y = 0;
      const g = new THREE.Group(); g.position.copy(pnt); g.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), f.N); body.add(g);
      const bright = new THREE.MeshStandardMaterial({ color: lin('#FFFFFF'), metalness: .88, roughness: .14, envMapIntensity: 2.8 });
      const face = new THREE.MeshStandardMaterial({ color: lin('#F2F3F5'), metalness: .85, roughness: .2, envMapIntensity: 2.6 });
      // domed pill (a squashed superellipsoid) so the chrome picks up long highlights like the real button
      const pill = new THREE.SphereGeometry(1, 64, 32), pa = pill.attributes.position;
      for (let i = 0; i < pa.count; i++) pa.setXYZ(i, spow(pa.getX(i), .42) * .08, spow(pa.getY(i), .6) * .029, pa.getZ(i) * .016);
      pill.computeVertexNormals();
      const pm = new THREE.Mesh(pill, bright); pm.position.z = .004; g.add(pm);
    })();

    // charging: Micro USB port under the button, a status light, and a cable that plugs in (battery chapter)
    const charge = (function () {
      const f = frames[Math.round(NS * .02)];
      const tipP = curve.getPointAt(0).clone().addScaledVector(f.N, f.off); const out = f.T.clone().negate();
      const port = tipP.clone().addScaledVector(out, -.006);
      const pg = new THREE.Group(); pg.position.copy(port); pg.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), out); pg.rotateZ(Math.PI / 2); body.add(pg);
      // rounded pocket recessed into the end cap, with the Micro USB socket inside
      const pocketRim = new THREE.Mesh(new THREE.ExtrudeGeometry(roundedShape(THREE, .128, .066, .033), { depth: .004, bevelEnabled: true, bevelThickness: .004, bevelSize: .004, bevelSegments: 4, curveSegments: 20 }), new THREE.MeshPhysicalMaterial({ color: lin('#C3C7CC'), roughness: .5 }));
      pg.add(pocketRim);
      const pocket = new THREE.Mesh(new THREE.ExtrudeGeometry(roundedShape(THREE, .112, .052, .026), { depth: .002, bevelEnabled: false, curveSegments: 20 }), new THREE.MeshStandardMaterial({ color: lin('#9FA4AB'), roughness: .6 }));
      pocket.position.z = .006; pg.add(pocket);
      const sock = new THREE.Shape(); sock.moveTo(-.026, .008); sock.lineTo(.026, .008); sock.lineTo(.02, -.008); sock.lineTo(-.02, -.008); sock.closePath();
      const socket = new THREE.Mesh(new THREE.ExtrudeGeometry(sock, { depth: .002, bevelEnabled: false }), new THREE.MeshStandardMaterial({ color: 0x15171a, roughness: .5 }));
      socket.position.z = .0085; pg.add(socket);
      const pins = new THREE.Mesh(new THREE.BoxGeometry(.036, .003, .002), new THREE.MeshStandardMaterial({ color: 0xd8c27a, metalness: 1, roughness: .3 }));
      pins.position.set(0, .002, .0106); pg.add(pins);
      // rubber cover with printed icons, hinged on one side; it swings open before the cable goes in
      const cc = document.createElement('canvas'); cc.width = 256; cc.height = 128; const x2 = cc.getContext('2d');
      x2.fillStyle = '#c9ccd1'; x2.fillRect(0, 0, 256, 128);
      x2.strokeStyle = '#7b8088'; x2.lineWidth = 7; x2.lineCap = 'round';
      x2.beginPath(); x2.arc(98, 66, 20, -Math.PI * .3, Math.PI * 1.3); x2.stroke(); x2.beginPath(); x2.moveTo(98, 40); x2.lineTo(98, 64); x2.stroke();
      x2.fillStyle = '#7b8088'; [[150, 50], [168, 66], [150, 82], [132, 66]].forEach(([u, v]) => { x2.beginPath(); x2.arc(u + 14, v, 6, 0, Math.PI * 2); x2.fill(); });
      const ct = new THREE.CanvasTexture(cc); ct.encoding = THREE.sRGBEncoding; ct.repeat.set(1 / .13, 1 / .068); ct.offset.set(.5, .5);
      const hinge = new THREE.Group(); hinge.position.set(.065, 0, .011); pg.add(hinge);
      const cover = new THREE.Mesh(new THREE.ExtrudeGeometry(roundedShape(THREE, .13, .068, .034), { depth: .006, bevelEnabled: true, bevelThickness: .006, bevelSize: .005, bevelSegments: 5, curveSegments: 20 }), new THREE.MeshPhysicalMaterial({ map: ct, roughness: .55, clearcoat: .15 }));
      cover.position.x = -.065; hinge.add(cover);
      const tether = new THREE.Mesh(new THREE.BoxGeometry(.012, .018, .01), new THREE.MeshPhysicalMaterial({ color: lin('#C9CCD1'), roughness: .6 }));
      tether.position.set(.004, 0, -.004); hinge.add(tether);
      const ledMat = new THREE.MeshBasicMaterial({ color: 0x220404 });
      const lf = frames[Math.round(NS * .06)];
      const ledPos = lf.p.clone().addScaledVector(lf.N, lf.off + lf.w * .12); ledPos.y = lf.h / 2 + .0015;
      const led = new THREE.Mesh(new THREE.CircleGeometry(.011, 24), ledMat); led.position.copy(ledPos); led.rotation.x = -Math.PI / 2; led.visible = false; // no status light on the real unit
      const ledGlow = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowTex('rgba(255,70,60,1)', 'rgba(255,30,30,.35)'), blending: THREE.AdditiveBlending, transparent: true, depthWrite: false, opacity: 0 }));
      ledGlow.scale.set(.12, .12, 1); ledGlow.position.copy(ledPos);

      // cable: plug + strain relief + tube, built in plug space (+z points into the port)
      const rig = new THREE.Group(); rig.visible = false; body.add(rig);
      rig.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), out.clone().negate()); // +z into the port, in body space
      const plugTurn = new THREE.Group(); plugTurn.rotation.z = Math.PI / 2; rig.add(plugTurn);
      const cableMat = new THREE.MeshPhysicalMaterial({ color: lin('#E9EBEE'), roughness: .45, clearcoat: .35 });
      const reliefMat = new THREE.MeshPhysicalMaterial({ color: lin('#C9CDD2'), roughness: .6 });
      const plugBody = new THREE.Mesh(new THREE.ExtrudeGeometry(roundedShape(THREE, .092, .05, .02), { depth: .15, bevelEnabled: true, bevelThickness: .012, bevelSize: .01, bevelSegments: 5, curveSegments: 16 }), cableMat);
      plugBody.position.z = -.17; plugTurn.add(plugBody);
      const tipMetal = new THREE.Mesh(new THREE.BoxGeometry(.05, .016, .05), new THREE.MeshStandardMaterial({ color: 0xffffff, metalness: 1, roughness: .15 }));
      tipMetal.position.z = .01; plugTurn.add(tipMetal);
      const relief = new THREE.Mesh(new THREE.CylinderGeometry(.018, .028, .09, 24), reliefMat); relief.rotation.x = Math.PI / 2; relief.position.z = -.235; rig.add(relief);
      const path = new THREE.CatmullRomCurve3([new THREE.Vector3(0, 0, -.27), new THREE.Vector3(0, -.02, -.55), new THREE.Vector3(0, -.2, -.85), new THREE.Vector3(.08, -.6, -1.1), new THREE.Vector3(.2, -1.2, -1.25), new THREE.Vector3(.3, -2.2, -1.3)]);
      rig.add(new THREE.Mesh(new THREE.TubeGeometry(path, 120, .014, 12, false), cableMat));
      const pulseTex = glowTex('rgba(190,255,210,1)', 'rgba(60,220,120,.45)');
      const pulses = [0, 1, 2].map(() => { const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: pulseTex, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false, opacity: 0 })); sp.scale.set(.07, .07, 1); rig.add(sp); return sp; });
      const ease = (x) => 1 - Math.pow(1 - x, 3);
      let plugged = -1;
      return function (k, t) {
        if (k <= .005) { hinge.rotation.y = 0; rig.visible = false; plugged = -1; ledMat.color.setHex(0x220404); ledGlow.material.opacity = 0; return; }
        hinge.rotation.y = 1.95 * ease(Math.min(1, k / .4));
        const kp = Math.max(0, (k - .3) / .7);
        rig.visible = kp > .005;
        const d = lerp(.75, 0, ease(Math.min(1, kp)));
        rig.position.copy(port).addScaledVector(out, d + .012);
        const inPort = k > .97;
        if (inPort && plugged < 0) plugged = t;
        if (!inPort) plugged = -1;
        const el = plugged < 0 ? 0 : t - plugged;
        const charging = inPort && el < 3.2;
        const on = !inPort ? 0 : charging ? (Math.sin(el * Math.PI * 3.2) > 0 ? 1 : .15) : 1;
        ledMat.color.setRGB(.13 + .87 * on, .02 + .08 * on, .02 + .06 * on);
        ledGlow.material.opacity = on * .9;
        pulses.forEach((sp, i) => {
          const ph = ((t * .55 + i / 3) % 1), pt = path.getPointAt(1 - ph);
          sp.position.copy(pt);
          sp.material.opacity = (charging ? 1 : 0) * Math.sin(ph * Math.PI) * k;
        });
      };
    })();

    if (opts.shadows) body.traverse((o) => { if (o.isMesh && !o.material.transparent) { o.castShadow = true; o.receiveShadow = true; } });

    return {
      body, padMat, glows, rings,
      setAccent(h, instant) { acFrom.copy(acCur); acTo.copy(toLin(h)); acT = instant ? 1 : 0; if (instant) { acCur.copy(acTo); paint(acCur); } },
      setCharge(k, t) { charge(k, t); },
      update(dt) { if (acT < 1) { acT = Math.min(1, acT + dt / .45); acCur.copy(acFrom).lerp(acTo, sm(0, 1, acT)); paint(acCur); } }
    };
  }

  /* fine plastic grain: a tiling normal map shared by all models (canvas cached, texture per renderer) */
  let grainCanvas = null;
  function grain(THREE, repeat = 6) {
    if (!grainCanvas) {
      const N = 256, h = new Float32Array(N * N);
      let seed = 11; const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
      for (let i = 0; i < N * N; i++) h[i] = rnd();
      for (let pass = 0; pass < 2; pass++) { const c = new Float32Array(h); for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) { let s2 = 0; for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) s2 += c[((y + dy + N) % N) * N + (x + dx + N) % N]; h[y * N + x] = s2 / 9; } }
      grainCanvas = document.createElement('canvas'); grainCanvas.width = grainCanvas.height = N;
      const g = grainCanvas.getContext('2d'), img = g.createImageData(N, N);
      for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
        const dx = (h[y * N + (x + 1) % N] - h[y * N + (x - 1 + N) % N]) * 3, dy = (h[((y + 1) % N) * N + x] - h[((y - 1 + N) % N) * N + x]) * 3;
        const l = Math.hypot(dx, dy, 1), o = (y * N + x) * 4;
        img.data[o] = (-dx / l * .5 + .5) * 255; img.data[o + 1] = (-dy / l * .5 + .5) * 255; img.data[o + 2] = (1 / l * .5 + .5) * 255; img.data[o + 3] = 255;
      }
      g.putImageData(img, 0, 0);
    }
    const t = new THREE.CanvasTexture(grainCanvas); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(repeat, repeat); return t;
  }
  // a HOVE wordmark on a transparent canvas, for printed or debossed logos
  function logoCanvas(color = '#000', w = 1116, h = 262) {
    const c = document.createElement('canvas'); c.width = w; c.height = h + 20; const g = c.getContext('2d');
    g.translate(0, 10); g.strokeStyle = color; g.lineWidth = 30; g.lineCap = 'round'; g.lineJoin = 'round';
    ['M16 16V244M226 16V244M16 116H226', 'M580 18L705 240L830 18', 'M1090 18H902V242H1100M902 130H1075'].forEach((d) => g.stroke(new Path2D(d)));
    g.lineCap = 'butt'; g.stroke(new Path2D('M300 104A136 136 0 0 1 554 104'));
    g.lineCap = 'round'; g.lineWidth = 26; g.stroke(new Path2D('M296 158 Q304 222 370 242 M558 158 Q550 222 484 242'));
    return c;
  }

  window.HoveUnit = { build, studioEnv, roundedShape, grain, logoCanvas };

  /* ---------- neckband as a stage model: poses, remote and effects ---------- */
  const NECK_POSES = {
    hero:        { mpy: .14, rx: 1.22, ry: 0,     rz: 0,    px: 0,     py: .36,  s: 1.18, spin: 1 },
    showcase:    { rx: 1.1,  ry: 0,     rz: 0,    px: 0,     py: -.2,  s: 1.25, spin: 1 },
    design:      { rx: .78,  ry: -.8,   rz: .16,  px: 1,     py: 0,    s: 1.08 },
    design_left: { rx: .78,  ry: .8,    rz: -.16, px: -1,    py: 0,    s: 1.08 },
    heat:        { rx: .34,  ry: 0,     rz: 0,    px: 0,     py: -.55, s: 1.75, heat: 1 },
    pulse:       { rx: .34,  ry: .32,   rz: 0,    px: 0,     py: -.55, s: 1.75, heat: .15, pulse: 1 },
    hidden:      { rx: .5,   ry: .9,    rz: 0,    px: 0,     py: -.3,  s: 1.2,  op: 0 },
    fit:         { rx: 1.5708, ry: 0,   rz: 0,    px: -1,    py: 0,    s: 1.12 },
    fit_right:   { rx: 1.5708, ry: 0,   rz: 0,    px: 1,     py: 0,    s: 1.12 },
    controls:    { rx: .32,  ry: -1.08, rz: 0,    px: -1.05, py: -.05, s: 1.2,  remote: 1 },
    battery:     { rx: .5,   ry: .35,   rz: 0,    px: 1,     py: .1,   s: 1.3,  charge: 1 },
    charge:      { rx: .5,   ry: .35,   rz: 0,    px: 1,     py: .1,   s: 1.3,  charge: 1 },
    buy:         { rx: 1.2,  ry: .5,    rz: -.05, px: -1,    py: .1,   s: .98,  drag: 1 },
    buy_faded:   { rx: 1.2,  ry: .5,    rz: -.05, px: -1,    py: .1,   s: .98,  drag: 1, op: 0 }
  };
  window.HoveModels = window.HoveModels || {};
  window.HoveModels.neckband = {
    euler: 'yx', fit: 3.1, mobileScale: .45, miniPose: 'showcase',
    defaults: { heat: 0, pulse: 0, remote: 0, charge: 0 },
    poses: NECK_POSES,
    build(THREE, opts) {
      const u = build(THREE, opts);
      const lin = (h) => new THREE.Color(h).convertSRGBToLinear();
      const whiteMat = new THREE.MeshPhysicalMaterial({ color: lin('#F1F2F3'), roughness: .34, clearcoat: .55, clearcoatRoughness: .28 });
      const remote = new THREE.Group();
      if (!opts.mini) {
        remote.add(new THREE.Mesh(new THREE.ExtrudeGeometry(roundedShape(THREE, .46, .82, .13), { depth: .05, bevelEnabled: true, bevelThickness: .03, bevelSize: .03, bevelSegments: 6, curveSegments: 24 }), whiteMat));
        const c = document.createElement('canvas'); c.width = 460; c.height = 820; const g = c.getContext('2d');
        const btn = (x, y) => { g.fillStyle = '#C9CDD2'; g.beginPath(); g.roundRect ? g.roundRect(x, y, 150, 150, 34) : g.rect(x, y, 150, 150); g.fill(); };
        btn(64, 250); btn(246, 250); btn(64, 432); btn(246, 432);
        g.fillStyle = '#F7F7F8'; g.strokeStyle = '#F7F7F8'; g.lineWidth = 12; g.lineCap = 'round';
        for (let i = 0; i < 3; i++) { g.beginPath(); const x = 106 + i * 33; g.moveTo(x, 292); g.bezierCurveTo(x - 16, 312, x + 16, 326, x, 346); g.stroke(); }
        g.beginPath(); g.moveTo(104, 362); g.lineTo(174, 362); g.stroke();
        g.beginPath(); g.arc(321, 325, 42, 0, Math.PI * 2); g.lineWidth = 9; g.stroke();
        g.font = '600 44px Arial'; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('M', 321, 327);
        g.lineWidth = 13; g.beginPath(); g.moveTo(106, 507); g.lineTo(172, 507); g.moveTo(139, 474); g.lineTo(139, 540); g.stroke();
        g.beginPath(); g.moveTo(288, 507); g.lineTo(354, 507); g.stroke();
        g.fillStyle = '#9AA0A8'; g.beginPath(); g.arc(230, 150, 8, 0, Math.PI * 2); g.fill();
        const t = new THREE.CanvasTexture(c); t.encoding = THREE.sRGBEncoding; t.anisotropy = 4;
        const face = new THREE.Mesh(new THREE.PlaneGeometry(.46, .82), new THREE.MeshStandardMaterial({ map: t, transparent: true, roughness: .5 }));
        face.position.z = .05 + .03 + .002; remote.add(face);
      }
      remote.visible = false;
      return {
        body: u.body, extras: remote, setAccent: u.setAccent,
        update(dt, t, cur, ctx) {
          u.update(dt);
          u.setCharge(cur.charge || 0, t);
          const heat = cur.heat || 0, pulse = cur.pulse || 0;
          u.padMat.emissiveIntensity = heat * (.55 + .2 * Math.sin(t * 2.4));
          u.glows.forEach((g) => { g.material.opacity = heat * (.75 + .25 * Math.sin(t * 2.4)); });
          u.rings.forEach(({ m, k }) => { const ph = (t * .9 + k / 3) % 1; m.scale.setScalar(.12 + ph * .55); m.material.opacity = pulse * (1 - ph) * .85; });
          const rv = cur.remote || 0;
          remote.visible = rv > .01 && !ctx.mini;
          if (remote.visible) {
            const sf = ctx.sf, mobile = ctx.mobile;
            const rxp = mobile ? ctx.px + .75 * sf : ctx.px + 1.45 * sf;
            remote.position.set(rxp + (1 - rv) * 3, ctx.py - .25 + (mobile ? -.1 : 0), .6);
            remote.rotation.set(-.35, -.45 + (1 - rv) * 1.5, .12);
            remote.scale.setScalar(sf * (mobile ? .75 : 1));
          }
        }
      };
    }
  };
})();
